import { Link } from "react-router-dom";
import { Star, ArrowUpRight, BadgeCheck } from "lucide-react";
import { REVIEWS } from "@/data/reviews";
import { useLiveStats } from "@/lib/liveStats";
import { REVIEWS as TIDS } from "@/constants/testIds";
import ReviewCard from "@/components/ReviewCard";

const Stars = ({ size = "w-3.5 h-3.5" }) => (
  <span className="inline-flex items-center gap-0.5" aria-label="5 out of 5 stars">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${size} fill-[#B8923A] text-[#B8923A]`} strokeWidth={1} />
    ))}
  </span>
);

const Verified = ({ source }) => (
  <span
    className="inline-flex items-center gap-1.5 text-stone-500"
    title={`Verified review on ${source}`}
  >
    <BadgeCheck className="w-[15px] h-[15px] text-[#7BA478]" strokeWidth={1.6} />
    <span className="font-serif italic text-[0.92rem]">verified on {source}</span>
  </span>
);

const Attribution = ({ r, testId }) => (
  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
    <span className="font-serif italic text-stone-800 text-[1.08rem]">{r.name}</span>
    <span className="text-stone-300">·</span>
    <span className="overline text-stone-500">{r.date}</span>
    <span className="text-stone-300">·</span>
    <a
      href={r.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="inline-flex items-center hover:text-stone-900 transition-colors"
    >
      <Verified source={r.source} />
    </a>
  </div>
);

export default function Reviews({ limit = 7, location = "Essex" }) {
  const stats = useLiveStats();
  // Show a curated subset on the homepage; full list lives at /reviews
  const featured = REVIEWS[0];
  const rest = REVIEWS.slice(1, limit);

  return (
    <section
      id="reviews"
      data-testid={TIDS.section}
      className="py-24 md:py-40 bg-stone-50 relative"
    >
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-20 md:mb-28">
          <p className="overline mb-8">Reviews. Verified. Source-linked.</p>
          <h2 className="font-serif text-[2.5rem] sm:text-[3.4rem] md:text-[4.8rem] leading-[1.04] tracking-tight text-stone-900">
            What real couples
            <br />
            <em className="italic font-normal text-stone-600">actually said.</em>
          </h2>
          <div className="mt-10 flex flex-col items-center gap-3">
            <Stars size="w-4 h-4" />
            <p className="font-serif text-stone-900 text-lg md:text-xl">
              <span className="kbd text-xl md:text-2xl">{stats.rating.toFixed(1)}</span>
              <span className="italic text-stone-500 ml-2">
                from {stats.total} reviews across Hitched, Google, Facebook, Bridebook and Guides for Brides
              </span>
            </p>
          </div>
        </div>

        {/* Featured pull quote */}
        <article data-testid={TIDS.card(0)} className="relative mb-24 md:mb-36">
          <div className="absolute -top-10 md:-top-20 left-1/2 -translate-x-1/2 font-serif text-[10rem] md:text-[16rem] leading-none text-stone-200 select-none pointer-events-none">
            &ldquo;
          </div>
          <div className="relative text-center pt-4 md:pt-8">
            <blockquote className="font-serif italic text-[1.7rem] sm:text-[2.1rem] md:text-[2.8rem] leading-[1.24] tracking-tight text-stone-900 max-w-3xl mx-auto">
              {featured.text}
            </blockquote>
            <div className="mt-12 flex flex-col items-center gap-3">
              <span className="font-serif italic text-stone-800 text-xl">{featured.name}</span>
              <a
                href={featured.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={TIDS.source(0)}
                className="hover:text-stone-900 transition-colors"
              >
                <Verified source={featured.source} />
              </a>
            </div>
          </div>
        </article>

        {/* Editorial card grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {rest.map((r, i) => (
            <ReviewCard
              key={`${r.name}-${r.iso}-${i}`}
              r={r}
              testId={TIDS.card(i + 1)}
              sourceTestId={TIDS.source(i + 1)}
            />
          ))}
        </div>

        {/* CTA to all reviews page */}
        <div className="mt-12 md:mt-16 text-center">
          <Link
            to="/reviews"
            data-testid={TIDS.viewAll}
            className="btn-primary"
          >
            View all {stats.total} reviews
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
          <p className="mt-6 font-serif italic text-stone-500 text-base">
            Every review on the next page is real, dated, and links back to its source.
          </p>
        </div>
      </div>
    </section>
  );
}
